// Kupon kodu servisi.
//
// Tasarim notu: kuponun Firestore dokuman kimligi = kupon kodunun kendisi
// (buyuk harfe cevrilmis). Boylece guvenlik kurallarinda "get" (tek dokuman)
// ile "list" (sorgu) ayrilabiliyor:
//   - Musteri yalnizca kodu BILDIGI kuponu okuyabiliyor
//   - Kuponlarin tamamini listelemek sadece yonetime acik
// Kod dokuman kimligi olmasaydi, herkes butun kupon kodlarini cekebilirdi.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
  runTransaction,
  Timestamp,
  orderBy,
  query,
} from 'firebase/firestore';
import { db } from '../firebase';

export type CouponType = 'percent' | 'free_shipping';

export const COUPON_TYPE_LABELS: Record<CouponType, string> = {
  percent: 'Yüzde İndirim',
  free_shipping: 'Ücretsiz Kargo',
};

export interface Coupon {
  code: string;
  type: CouponType;
  value: number;          // percent icin yuzde (1-100), free_shipping icin 0
  totalQuantity: number;  // kac kez kullanilabilir
  usedCount: number;
  minAmount: number;      // bu tutarin altindaki sepetlerde gecersiz (0 = sinir yok)
  isActive: boolean;
  expiresAt?: string | null; // YYYY-MM-DD
  createdAt?: Timestamp | Date;
}

export interface CouponValidation {
  valid: boolean;
  error?: string;
  coupon?: Coupon;
  discount?: number;      // urun toplamindan dusulecek tutar
  freeShipping?: boolean;
}

export function normalizeCode(code: string): string {
  return code.trim().toUpperCase().replace(/\s+/g, '');
}

// --- Yonetim ---

export async function createCoupon(
  input: Omit<Coupon, 'usedCount' | 'createdAt'>
): Promise<{ success: boolean; error?: string }> {
  const code = normalizeCode(input.code);

  if (!code) return { success: false, error: 'Kupon kodu boş olamaz.' };
  if (!/^[A-Z0-9-]{3,24}$/.test(code)) {
    return { success: false, error: 'Kod 3-24 karakter olmalı; sadece harf, rakam ve tire kullanın.' };
  }
  if (input.type === 'percent' && (input.value < 1 || input.value > 100)) {
    return { success: false, error: 'İndirim yüzdesi 1 ile 100 arasında olmalı.' };
  }
  if (input.totalQuantity < 1) {
    return { success: false, error: 'Adet en az 1 olmalı.' };
  }

  try {
    const ref = doc(db, 'coupons', code);
    const existing = await getDoc(ref);
    if (existing.exists()) {
      return { success: false, error: 'Bu kod zaten kullanılıyor.' };
    }

    await setDoc(ref, {
      code,
      type: input.type,
      value: input.type === 'free_shipping' ? 0 : input.value,
      totalQuantity: input.totalQuantity,
      usedCount: 0,
      minAmount: input.minAmount || 0,
      isActive: input.isActive !== false,
      expiresAt: input.expiresAt || null,
      createdAt: Timestamp.now(),
    });

    return { success: true };
  } catch (error: any) {
    console.error('Kupon olusturulamadi:', error);
    return { success: false, error: error.message };
  }
}

export async function getCoupons(): Promise<Coupon[]> {
  try {
    const snapshot = await getDocs(query(collection(db, 'coupons'), orderBy('createdAt', 'desc')));
    return snapshot.docs.map(d => ({ ...(d.data() as Coupon), code: d.id }));
  } catch (error) {
    console.error('Kuponlar getirilemedi:', error);
    return [];
  }
}

export async function setCouponActive(code: string, isActive: boolean): Promise<boolean> {
  try {
    await updateDoc(doc(db, 'coupons', normalizeCode(code)), { isActive });
    return true;
  } catch (error) {
    console.error('Kupon durumu degistirilemedi:', error);
    return false;
  }
}

export async function deleteCoupon(code: string): Promise<boolean> {
  try {
    await deleteDoc(doc(db, 'coupons', normalizeCode(code)));
    return true;
  } catch (error) {
    console.error('Kupon silinemedi:', error);
    return false;
  }
}

// --- Musteri tarafi ---

// Kuponu dogrular ve indirimi hesaplar. Henuz kullanim hakkini DUSMEZ;
// dusme islemi odeme basarili olunca redeemCoupon ile yapilir.
export async function validateCoupon(rawCode: string, subtotal: number): Promise<CouponValidation> {
  const code = normalizeCode(rawCode);
  if (!code) return { valid: false, error: 'Kupon kodu giriniz.' };

  try {
    const snapshot = await getDoc(doc(db, 'coupons', code));

    if (!snapshot.exists()) {
      return { valid: false, error: 'Kupon kodu bulunamadı.' };
    }

    const coupon = { ...(snapshot.data() as Coupon), code };

    if (!coupon.isActive) {
      return { valid: false, error: 'Bu kupon şu anda kullanıma kapalı.' };
    }
    if (coupon.usedCount >= coupon.totalQuantity) {
      return { valid: false, error: 'Bu kuponun kullanım hakkı dolmuş.' };
    }
    if (coupon.expiresAt) {
      const today = new Date().toISOString().slice(0, 10);
      if (coupon.expiresAt < today) {
        return { valid: false, error: 'Bu kuponun süresi dolmuş.' };
      }
    }
    if (coupon.minAmount > 0 && subtotal < coupon.minAmount) {
      return {
        valid: false,
        error: `Bu kupon en az ${coupon.minAmount.toLocaleString('tr-TR')} TL tutarındaki sepetlerde geçerli.`,
      };
    }

    if (coupon.type === 'free_shipping') {
      return { valid: true, coupon, discount: 0, freeShipping: true };
    }

    const discount = Math.round((subtotal * coupon.value) / 100);
    return { valid: true, coupon, discount, freeShipping: false };
  } catch (error: any) {
    console.error('Kupon dogrulanamadi:', error);
    return { valid: false, error: 'Kupon kontrol edilemedi.' };
  }
}

// Kullanim hakkini bir azaltir. Odeme basarili olduktan sonra cagrilir.
// Transaction kullanilir ki ayni kuponu ayni anda kullanan iki kisi
// toplam adedi asamasin.
export async function redeemCoupon(rawCode: string): Promise<boolean> {
  const code = normalizeCode(rawCode);
  if (!code) return false;

  try {
    await runTransaction(db, async (transaction) => {
      const ref = doc(db, 'coupons', code);
      const snapshot = await transaction.get(ref);

      if (!snapshot.exists()) throw new Error('Kupon bulunamadi');

      const data = snapshot.data() as Coupon;
      if (data.usedCount >= data.totalQuantity) {
        throw new Error('Kupon hakki dolmus');
      }

      transaction.update(ref, { usedCount: data.usedCount + 1 });
    });

    return true;
  } catch (error) {
    console.error('Kupon kullanimi islenemedi:', error);
    return false;
  }
}
